/** @jsx jsx */
import ExhibitorPage from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorPage/ExhibitorPage';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import {
  getCertificationsForId,
  extractPartnerIdsForCharacteristics,
} from '@bottlebooks/gatsby-theme-event-pages/src/helpers/brandCertifications';
import deepMerge from 'lodash/merge';
import getWfsPrice from './getWfsPrice';

export default function ExhibitorTemplate({
  data,
  pageContext,
  location,
  navigate,
}) {
  const { previous, next } = data;
  const { site, exhibitor, event } = afterQuery(data);
  return (
    <ExhibitorPage
      {...pageContext}
      event={event}
      exhibitor={exhibitor}
      products={exhibitor && exhibitor.products}
      previous={previous}
      next={next}
      siteTitle={site.siteMetadata.title}
      location={location}
      navigate={navigate}
    />
  );
}

export const pageQuery = graphql`
  fragment wfs_ExhibitorTemplate_Bottlebooks_Event on Bottlebooks_Event {
    registeredBrands(filter: { companyId: { eq: $exhibitorId } }) {
      nodes {
        ...bb_ExhibitorPage_RegisteredBrands
      }
    }
    registeredProducts(filter: { partnerId: { eq: $exhibitorId } }) {
      ...brandCertifications_RegisteredProductConnection
      nodes {
        productId
        wfsPrice: customFieldValue(key: "wfsPrice") {
          ...FieldValue
        }
      }
    }
    registrations(filter: { companyId: { eq: $exhibitorId } }) {
      nodes {
        forceHideBrands: customFieldValue(key: "forceHideBrands") {
          ...FieldValue
        }
        customShortDescription: customFieldValue(
          key: "__inject__shortDescription"
        ) {
          ... on Bottlebooks_TextFieldValue {
            value
          }
        }
        exhibitorType: customFieldValue(key: "exhibitorType") {
          __typename
          ... on Bottlebooks_SelectFieldValue {
            key
          }
        }
        ...bb_Registration
        ...bb_ExhibitorPage_SingleRegistration
        profile {
          logo {
            publicId
          }
        }
      }
    }
    exhibitor(exhibitorId: $exhibitorId) {
      ...bb_ExhibitorPage
    }
  }
  query wfs_ExhibitorTemplate(
    $id: String!
    $eventId: ID!
    $exhibitorId: ID!
    $locale: Bottlebooks_ContentLocale
    $previousId: String
    $nextId: String
  ) {
    bottlebooks {
      event(eventId: $eventId, locale: $locale) {
        ...wfs_ExhibitorTemplate_Bottlebooks_Event
      }
    }

    exhibitor(id: { eq: $id }) {
      ...ExhibitorTemplate_Exhibitor
    }
    previous: exhibitor(id: { eq: $previousId }) {
      ...ChangeExhibitorLink
    }
    next: exhibitor(id: { eq: $nextId }) {
      ...ChangeExhibitorLink
    }
    ...ExhibitorTemplate_Query
  }

  fragment ExhibitorTemplate_Exhibitor on Exhibitor {
    ...ExhibitorPage
    event {
      ...ExhibitorPage_Event
    }
  }

  fragment ExhibitorTemplate_Query on Query {
    site {
      siteMetadata {
        title
      }
    }
  }
`;

function afterQuery({ bottlebooks, event, exhibitor, site }) {
  const bb_exhibitor = bottlebooks?.event?.exhibitor;
  const bb_registration = bottlebooks?.event?.registrations?.nodes?.[0];
  const bb_registeredBrands = bottlebooks?.event?.registeredBrands?.nodes;
  const bb_registeredProducts = bottlebooks?.event?.registeredProducts?.nodes;
  const bb_products = bb_exhibitor?.products?.nodes;
  const exhibitorType = bb_registration.exhibitorType.key;

  const products = exhibitor.products?.map((product, index) => {
    if (!bb_products?.length) return product;
    const bb_registeredProduct = bb_registeredProducts?.find(
      ({ productId }) => productId === product.productId
    );
    const price = bb_registeredProduct?.wfsPrice?.text;
    return {
      ...product,
      ...bb_products[index],
      ...bb_registration?.registeredProducts?.nodes?.[index]?.product,
      ...getWfsPrice({ exhibitorType, price }),
    };
  });
  const enhancedExhibitor = deepMerge(
    {},
    exhibitor,
    bb_exhibitor,
    bb_registration,
    bb_registration.profile,
    getCertificationsForId({
      extractIdsFn: extractPartnerIdsForCharacteristics,
      id: exhibitor.exhibitorId,
      event: bottlebooks?.event,
    }),
    {
      brands: bb_registeredBrands,
      products,
    },
    {
      shortDescription:
        bb_registration?.customShortDescription?.value ||
        bb_exhibitor?.shortDescription,
    }
  );
  return { site, event, exhibitor: enhancedExhibitor };
}
